import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext'; // @@CONTACTS_CLIENTS_v1@@

const BLANK = {
  first_name: '', last_name: '', email: '', phone: '',
  company: '', title: '', address: '', notes: '', tags: '',
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [msg, setMsg] = useState('');
  const [showForm, setShowForm] = useState(false);
  // @@CONTACTS_EDIT_v1@@ edit-contact modal state
  const [editing, setEditing] = useState(null);   // contact being edited (or null)
  const [editForm, setEditForm] = useState(BLANK);
  const [editBusy, setEditBusy] = useState(false);
  // @@CONTACTS_CLIENTS_v1@@ linked-clients attach/detach
  const { user } = useAuth();
  const canManage = !!user && ['admin', 'manager', 'staff'].includes(user.role);
  const [allClients, setAllClients] = useState([]);
  const [clientBusy, setClientBusy] = useState(false);

  async function load() {
    const res = await apiFetch('/api/contacts');
    if (res.ok) { const d = await res.json(); setContacts(d.contacts); }
  }
  useEffect(() => { load(); }, []);
  // @@CONTACTS_CLIENTS_v1@@ fetch the client list once for the attach dropdown (MANAGE only).
  useEffect(() => {
    if (!canManage) return;
    apiFetch('/api/clients')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Array.isArray(d.clients)) setAllClients(d.clients); })
      .catch(() => {});
  }, [canManage]);

  async function create() {
    setMsg('');
    const payload = { ...form, tags: form.tags };
    const res = await apiFetch('/api/contacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) { setForm(BLANK); setShowForm(false); load(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not create contact.'); }
  }

  // @@CONTACTS_CLIENTS_v1@@ attach a client to the contact being edited.
  async function attachClient(clientId) {
    if (!editing || !clientId) return;
    setMsg(''); setClientBusy(true);
    try {
      const res = await apiFetch(`/api/clients/${clientId}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: editing.id }),
      });
      if (res.ok) { await refreshEditing(); }
      else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not attach client.'); }
    } catch (e) { setMsg('Could not reach the server.'); }
    finally { setClientBusy(false); }
  }

  async function detachClient(clientId) {
    if (!editing || !clientId) return;
    setMsg(''); setClientBusy(true);
    try {
      const res = await apiFetch(`/api/clients/${clientId}/contacts/${editing.id}`, { method: 'DELETE' });
      if (res.ok) { await refreshEditing(); }
      else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not remove client.'); }
    } catch (e) { setMsg('Could not reach the server.'); }
    finally { setClientBusy(false); }
  }

  // Reload the list and re-point `editing` at the refreshed row so the modal
  // chips reflect the new client set without closing.
  async function refreshEditing() {
    const res = await apiFetch('/api/contacts');
    if (!res.ok) return;
    const d = await res.json();
    const list = d.contacts || [];
    setContacts(list);
    if (editing) {
      const fresh = list.find((c) => c.id === editing.id);
      if (fresh) setEditing(fresh);
    }
  }

  // @@CONTACTS_EDIT_v1@@ open the edit modal, hydrating the form from a contact row.
  function openEdit(c) {
    setMsg('');
    setEditing(c);
    setEditForm({
      first_name: c.first_name || '', last_name: c.last_name || '',
      email: c.email || '', phone: c.phone || '',
      company: c.company || '', title: c.title || '',
      address: c.address || '', notes: c.notes || '',
      tags: Array.isArray(c.tags) ? c.tags.join(', ') : (c.tags || ''),
    });
  }

  async function saveEdit() {
    if (!editing) return;
    setMsg(''); setEditBusy(true);
    try {
      const res = await apiFetch(`/api/contacts/${editing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (res.ok) { setEditing(null); load(); }
      else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not save contact.'); }
    } catch (e) { setMsg('Could not reach the server.'); }
    finally { setEditBusy(false); }
  }

  // @@CLIENT_PORTAL_v1@@ invite a contact to the client portal.
  const [inviteBusy, setInviteBusy] = useState(null);
  async function invite(c) {
    setMsg('');
    setInviteBusy(c.id);
    try {
      const res = await apiFetch(`/api/contacts/${c.id}/invite`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg(d.warning ? d.warning : (d.invite_sent ? 'Invite sent.' : 'User ready, but email not sent (check RESEND_API_KEY).'));
        load();
      } else { setMsg(d.error || 'Could not send invite.'); }
    } catch (e) { setMsg('Could not reach the server.'); }
    finally { setInviteBusy(null); }
  }

  function fmtDate(s) {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString(); } catch { return '—'; }
  }

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Contacts</h1>
        <p>Your shared contact directory.</p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>{showForm ? 'New contact' : 'Add a contact'}</h2>
          <button className="hq-btn" onClick={() => setShowForm((s) => !s)}>
            {showForm ? 'Cancel' : 'New contact'}
          </button>
        </div>
        {showForm && (
          <div style={{ padding: 20 }}>
            <div className="hq-form">
              <input className="hq-input" placeholder="First name" value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <input className="hq-input" placeholder="Last name" value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              <input className="hq-input" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className="hq-input" placeholder="Phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input className="hq-input" placeholder="Company" value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <input className="hq-input" placeholder="Title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <input className="hq-input" placeholder="Address" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} style={{ minWidth: 240 }} />
              <input className="hq-input" placeholder="Tags (comma-separated)" value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })} style={{ minWidth: 200 }} />
              <input className="hq-input" placeholder="Notes" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ minWidth: 240 }} />
              <button className="hq-btn" onClick={create}>Save</button>
            </div>
            {msg && <p className="hq-msg">{msg}</p>}
          </div>
        )}
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head"><h2>All contacts</h2></div>
        <table className="hq-table">
          <thead>
            <tr><th>Name</th><th>Company</th><th>Email</th><th>Phone</th><th>Clients</th><th>Added</th><th>Portal</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id}>
                <td>{`${c.first_name} ${c.last_name}`.trim() || '—'}</td>
                <td>{c.company || '—'}{c.title ? ` · ${c.title}` : ''}</td>
                <td>{c.email || '—'}</td>
                <td>{c.phone || '—'}</td>
                <td>{/* @@CONTACTS_CLIENTS_v1@@ */}
                  {Array.isArray(c.clients) && c.clients.length
                    ? c.clients.map((cl) => cl.name).join(', ')
                    : '—'}</td>
                <td>{fmtDate(c.created_at)}</td>
                <td>
                  {c.portal_active ? (
                    <span className="hq-pill hq-on">Active</span>
                  ) : c.portal_invited ? (
                    <span className="hq-pill hq-off" style={{ marginRight: 8 }}>Invited</span>
                  ) : null}
                  {!c.portal_active && (
                    <button className="hq-btn" style={{ height: 28, padding: '0 10px', fontSize: 11 }}
                      disabled={!c.email || inviteBusy === c.id}
                      onClick={() => invite(c)}>
                      {inviteBusy === c.id ? '…' : (c.portal_invited ? 'Re-invite' : 'Invite')}
                    </button>
                  )}
                </td>
                <td>
                  {/* @@CONTACTS_EDIT_v1@@ */}
                  <button className="hq-btn" style={{ height: 28, padding: '0 12px', fontSize: 11 }}
                    onClick={() => openEdit(c)}>Edit</button>
                </td>
              </tr>
            ))}
            {contacts.length === 0 && (
              <tr><td className="hq-empty" colSpan={8}>No contacts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* @@CONTACTS_EDIT_v1@@ edit-contact modal */}
      {editing && (
        <div onClick={() => setEditing(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '60px 16px', zIndex: 50, overflowY: 'auto' }}>
          <div className="hq-panel" onClick={(e) => e.stopPropagation()}
            style={{ width: 560, maxWidth: '100%' }}>
            <div className="hq-panel-head">
              <h2>Edit contact</h2>
              <button className="hq-btn" onClick={() => setEditing(null)}>Close</button>
            </div>
            <div style={{ padding: 20 }}>
              <div className="hq-form">
                <input className="hq-input" placeholder="First name" value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
                <input className="hq-input" placeholder="Last name" value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
                <input className="hq-input" placeholder="Email" value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                <input className="hq-input" placeholder="Phone" value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                <input className="hq-input" placeholder="Company" value={editForm.company}
                  onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} />
                <input className="hq-input" placeholder="Title" value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
                <input className="hq-input" placeholder="Address" value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} style={{ minWidth: 240 }} />
                <input className="hq-input" placeholder="Tags (comma-separated)" value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} style={{ minWidth: 200 }} />
                <input className="hq-input" placeholder="Notes" value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} style={{ minWidth: 240 }} />
                <button className="hq-btn" onClick={saveEdit} disabled={editBusy}>
                  {editBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
              {/* @@CONTACTS_CLIENTS_v1@@ linked clients (MANAGE only) */}
              {canManage && (
                <div style={{ marginTop: 20, borderTop: '1px solid var(--hq-line)', paddingTop: 16 }}>
                  <div style={{ fontSize: 13, color: 'var(--hq-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.06em' }}>Linked clients</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {(editing.clients || []).length === 0 && (
                      <span style={{ color: 'var(--hq-muted)', fontSize: 13 }}>No clients linked.</span>
                    )}
                    {(editing.clients || []).map((cl) => (
                      <span key={cl.id} className="hq-pill hq-on"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {cl.name}
                        <button onClick={() => detachClient(cl.id)} disabled={clientBusy}
                          title="Remove" style={{ background: 'none', border: 'none', color: 'inherit',
                            cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                      </span>
                    ))}
                  </div>
                  <select className="hq-select" disabled={clientBusy} value=""
                    onChange={(e) => { const v = e.target.value; if (v) attachClient(v); }}>
                    <option value="">+ Add client…</option>
                    {allClients
                      .filter((cl) => !(editing.clients || []).some((x) => x.id === cl.id))
                      .map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
                  </select>
                </div>
              )}
              {msg && <p className="hq-msg">{msg}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
